import { Form, Head } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import OnboardingCharterSheet, {
  type FormalCharter,
} from "@/components/onboarding/onboarding-charter-sheet"
import OnboardingPrincipleCards, {
  type CharterValueCard,
} from "@/components/onboarding/onboarding-principle-cards"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import type { WizardStepKey } from "@/components/wizard/wizard-progress"
import { WizardShell } from "@/components/wizard/wizard-shell"
import { useOnline } from "@/hooks/use-online"
import { onboardingCharterPath } from "@/routes"

export interface WizardCharterBrand {
  id?: number
  name: string
  logo_url: string | null
  locale: "en" | "fr"
}

export interface WizardCharterProps {
  brand: WizardCharterBrand
  /** Server-computed required steps for the active brand. */
  required_steps: readonly WizardStepKey[]
  /** The three principle cards (eager, server-resolved from `charter_values`). */
  charter_values: CharterValueCard[]
  /**
   * The formal charter — an Inertia OPTIONAL prop, so `undefined` on first
   * paint; present only after the sheet-open partial reload
   * (`router.reload({ only: ['formal_charter'] })`).
   */
  formal_charter?: FormalCharter
}

/**
 * Wizard Step 2 — Brand-specific Crew Charter acceptance (OKS-459 reframe).
 *
 * The surface reads as "here's how this team plays": eyebrow + headline +
 * 3 brand-authored principle cards (data-driven from `charter_values`),
 * then a "Read the full charter →" link that opens the formal charter in a
 * `ui/drawer` bottom sheet. The formal `charter_body` loads lazily on sheet-open.
 *
 * **SINGLE acceptance source of truth** — the inline
 * `name="acceptance[charter]"` checkbox. The sheet's "I'll play by these"
 * action TICKS that checkbox + returns focus to Continue; it does NOT
 * auto-submit. NO TOS checkbox (TOS is a passive link at MinimalSignup).
 *
 * The inline rich-text charter body + standalone default banner (V1
 * OKS-384) are REMOVED — the formal version now lives entirely in the
 * sheet (the default-charter context folds into the sheet header).
 */
export default function Charter({
  brand,
  required_steps,
  charter_values,
  formal_charter,
}: WizardCharterProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [accepted, setAccepted] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const submitRef = useRef<HTMLButtonElement>(null)
  const checkboxRef = useRef<HTMLButtonElement>(null)
  const helperId = "wizard-charter-helper"

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  /**
   * Single acceptance: the sheet action ticks the inline checkbox. It does
   * NOT move focus — the sheet does, from its close-autofocus callback, so
   * there is exactly one focus writer (OKS-895). The previous version fired
   * a bare `requestAnimationFrame` here, which raced the drawer's own
   * restore-to-trigger and lost roughly whenever the frame boundary landed
   * first; the creator was left on "Read the full charter", where Enter
   * re-opens the charter they just accepted.
   */
  function handleSheetAccept() {
    setAccepted(true)
  }

  /**
   * Where focus goes after an accept, in priority order (UX Spec §2: one
   * defined destination per state).
   *
   * Continue is the destination. The ticked checkbox is the sole exception,
   * for the states where Continue is `disabled` and so cannot hold focus —
   * offline, or mid-submit. It is the control that proves the acceptance
   * registered, and it sits directly above the helper text explaining why
   * the step cannot complete yet. Never the trigger, never `<body>`.
   */
  function acceptFocusCandidates(): readonly (HTMLElement | null)[] {
    return [submitRef.current, checkboxRef.current]
  }

  return (
    <WizardShell brand={brand} step="charter" required_steps={required_steps}>
      <Head>
        <title>{t("wizard.charter.head_title", { brand: brand.name })}</title>
      </Head>
      <div data-testid="wizard-step-charter" className="flex flex-col gap-6">
        <OnboardingPrincipleCards
          brandName={brand.name}
          cards={charter_values}
          headingRef={headingRef}
        />

        <OnboardingCharterSheet
          brandName={brand.name}
          formalCharter={formal_charter}
          onAccept={handleSheetAccept}
          resolveAcceptFocusCandidates={acceptFocusCandidates}
        />

        <Form
          method="post"
          action={onboardingCharterPath()}
          className="flex flex-col gap-4"
          data-testid="wizard-charter-form"
        >
          {({ processing, errors }) => {
            const charterErrors = errors["acceptance.charter"]
            const baseErrors = errors.base
            const submitDisabled = processing || !online || !accepted
            return (
              <>
                {Array.isArray(baseErrors) && baseErrors.length > 0 && (
                  <Alert
                    variant="destructive"
                    data-testid="wizard-charter-error-base"
                  >
                    <AlertDescription>{baseErrors.join(", ")}</AlertDescription>
                  </Alert>
                )}

                <div className="flex items-start gap-3">
                  <Checkbox
                    ref={checkboxRef}
                    id="wizard-charter-accept"
                    checked={accepted}
                    onCheckedChange={(checked) => setAccepted(checked === true)}
                    aria-invalid={charterErrors ? "true" : undefined}
                    aria-describedby={
                      charterErrors ? "wizard-charter-error-charter" : undefined
                    }
                    data-testid="wizard-charter-accept-checkbox"
                    className="mt-0.5"
                  />
                  {/*
                    Hidden input mirrors the Base UI Checkbox state into the
                    form payload — Inertia <Form> serialises native form
                    controls; the Checkbox dispatches `data-checked` only.
                    Wire format: `acceptance[charter]=1` only when ticked
                    — Rails checkbox convention. Backend strict-checks
                    `params[:acceptance][:charter] == "1"` in
                    Onboarding::AcceptCharterService.
                  */}
                  {accepted && (
                    <input type="hidden" name="acceptance[charter]" value="1" />
                  )}
                  <div className="flex flex-1 flex-col gap-1">
                    <Label
                      htmlFor="wizard-charter-accept"
                      className="cursor-pointer text-sm leading-snug font-normal"
                    >
                      {t("wizard.charter.accept_label", { brand: brand.name })}
                    </Label>
                    <InputError
                      id="wizard-charter-error-charter"
                      messages={charterErrors}
                      data-testid="wizard-charter-error-charter"
                    />
                  </div>
                </div>

                {submitDisabled && !processing && online && (
                  <p
                    id={helperId}
                    className="text-muted-foreground text-xs"
                    role="status"
                    aria-live="polite"
                  >
                    {t("wizard.charter.submit_disabled_helper")}
                  </p>
                )}

                {!online && (
                  <p
                    className="text-muted-foreground text-sm"
                    role="status"
                    aria-live="polite"
                  >
                    {t("signup.errors.offline_helper")}
                  </p>
                )}

                <div className="bg-background sticky bottom-0 -mx-6 mt-2 flex flex-col gap-3 border-t px-6 py-4 sm:static sm:mx-0 sm:p-0">
                  <Button
                    ref={submitRef}
                    type="submit"
                    disabled={submitDisabled}
                    aria-disabled={submitDisabled}
                    aria-describedby={
                      submitDisabled && !processing && online
                        ? helperId
                        : undefined
                    }
                    data-testid="wizard-charter-submit"
                    size="lg"
                    // OKS-459 AC#5 — the CTA label interpolates the brand
                    // name ("Join {brand}'s crew"); a long brand name in
                    // the Button's base `whitespace-nowrap` overflows at
                    // 320px. Let it wrap vertically (grows, doesn't
                    // truncate — the branded copy stays intact) while
                    // keeping the ≥44px touch target via `min-h`.
                    className="h-auto min-h-11 w-full py-3 break-words whitespace-normal"
                  >
                    {processing && <Spinner className="size-4" />}
                    {processing
                      ? t("wizard.charter.submit_processing")
                      : t("wizard.charter.submit", { brand: brand.name })}
                  </Button>
                </div>
              </>
            )
          }}
        </Form>
      </div>
    </WizardShell>
  )
}
